import { createContext, useContext } from "react";

export interface ErpLayoutContextValue {
  /**
   * Request the sidebar panel to collapse.
   *
   * **Cleanup contract:** Callers MUST call `releaseCollapse` with the same
   * `source` string in a `useEffect` cleanup function. Failing to do so
   * leaves an orphaned source in the tracking Set, keeping the sidebar
   * collapsed permanently.
   *
   * @example
   * useEffect(() => {
   *   requestCollapse('focus-mode');
   *   return () => releaseCollapse('focus-mode');
   * }, [requestCollapse, releaseCollapse]);
   */
  requestCollapse: (source: string) => void;
  /** Release a previously requested collapse. See `requestCollapse` for details. */
  releaseCollapse: (source: string) => void;
  /** Whether the sidebar panel is currently collapsed (user preference OR programmatic). */
  isCollapsed: boolean;
  /** Hide the mobile top header bar (used by full-page detail views that have their own back nav). */
  setMobileHeaderHidden: (hidden: boolean) => void;
  /** Whether the mobile header is currently hidden. */
  mobileHeaderHidden: boolean;
}

export const ErpLayoutContext = createContext<
  ErpLayoutContextValue | undefined
>(undefined);

export function useErpLayout(): ErpLayoutContextValue {
  const context = useContext(ErpLayoutContext);
  if (!context) {
    return {
      requestCollapse: () => {},
      releaseCollapse: () => {},
      isCollapsed: false,
      setMobileHeaderHidden: () => {},
      mobileHeaderHidden: false,
    };
  }
  return context;
}
